import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { marketplaceaccountApi, type MarketplaceCode } from '../../api/marketplaceaccount';

const MARKETPLACES: { code: MarketplaceCode; label: string }[] = [
  { code: 'amazon_spapi', label: 'Amazon SP-API' },
  { code: 'flipkart', label: 'Flipkart' },
  { code: 'myntra', label: 'Myntra' },
  { code: 'ajio', label: 'AJIO' },
  { code: 'shopify', label: 'Shopify' },
  { code: 'woocommerce', label: 'WooCommerce' },
];

export function ConnectionWizardPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [marketplace, setMarketplace] = useState<MarketplaceCode>('amazon_spapi');
  const [accountCode, setAccountCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [region, setRegion] = useState('eu-west-1');
  const [lwaClientID, setLwaClientID] = useState('');
  const [lwaClientSecret, setLwaClientSecret] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function createAndConnect() {
    setLoading(true);
    setError('');
    try {
      const account = await marketplaceaccountApi.create({
        account_code: accountCode, marketplace, display_name: displayName,
        region: marketplace === 'amazon_spapi' ? region : undefined,
      });
      if (marketplace === 'amazon_spapi') {
        await marketplaceaccountApi.connect(account.id, {
          amazon_payload: {
            lwa_client_id: lwaClientID, lwa_client_secret: lwaClientSecret,
            refresh_token: refreshToken, region,
          },
        });
      }
      navigate('/marketplace-accounts/connections');
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl p-6">
      <h1 className="text-2xl font-semibold">Marketplace Connection Wizard</h1>
      <p className="mt-1 text-sm text-slate-600">Step {step} of 3</p>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

      {step === 1 && (
        <div className="mt-6 space-y-4">
          <label className="block text-sm font-medium">Marketplace</label>
          <select className="w-full rounded border p-2" value={marketplace} onChange={(e) => setMarketplace(e.target.value as MarketplaceCode)}>
            {MARKETPLACES.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
          </select>
          <button type="button" className="rounded bg-slate-900 px-4 py-2 text-white" onClick={() => setStep(2)}>Next</button>
        </div>
      )}

      {step === 2 && (
        <div className="mt-6 space-y-4">
          <input className="w-full rounded border p-2" placeholder="Account code" value={accountCode} onChange={(e) => setAccountCode(e.target.value)} />
          <input className="w-full rounded border p-2" placeholder="Display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          {marketplace === 'amazon_spapi' && (
            <input className="w-full rounded border p-2" placeholder="Region" value={region} onChange={(e) => setRegion(e.target.value)} />
          )}
          <div className="flex gap-2">
            <button type="button" className="rounded border px-4 py-2" onClick={() => setStep(1)}>Back</button>
            <button type="button" className="rounded bg-slate-900 px-4 py-2 text-white" onClick={() => setStep(3)}>Next</button>
          </div>
        </div>
      )}

      {step === 3 && marketplace === 'amazon_spapi' && (
        <div className="mt-6 space-y-4">
          <h2 className="font-medium">Amazon SP-API Credentials</h2>
          <input className="w-full rounded border p-2" placeholder="LWA Client ID" value={lwaClientID} onChange={(e) => setLwaClientID(e.target.value)} />
          <input className="w-full rounded border p-2" type="password" placeholder="LWA Client Secret" value={lwaClientSecret} onChange={(e) => setLwaClientSecret(e.target.value)} />
          <input className="w-full rounded border p-2" type="password" placeholder="Refresh Token" value={refreshToken} onChange={(e) => setRefreshToken(e.target.value)} />
          <div className="flex gap-2">
            <button type="button" className="rounded border px-4 py-2" onClick={() => setStep(2)}>Back</button>
            <button type="button" disabled={loading} className="rounded bg-slate-900 px-4 py-2 text-white disabled:opacity-50" onClick={() => void createAndConnect()}>
              {loading ? 'Connecting…' : 'Connect'}
            </button>
          </div>
        </div>
      )}

      {step === 3 && marketplace !== 'amazon_spapi' && (
        <div className="mt-6 space-y-4">
          <p className="text-sm text-slate-600">Create the account, then complete OAuth from the connections page.</p>
          <div className="flex gap-2">
            <button type="button" className="rounded border px-4 py-2" onClick={() => setStep(2)}>Back</button>
            <button type="button" disabled={loading} className="rounded bg-slate-900 px-4 py-2 text-white" onClick={() => void createAndConnect()}>
              {loading ? 'Creating…' : 'Create account'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
